/**
 * <deal-selector> custom element.
 * Owns the PDP deal-picker state and posts to /cart/add.js with line item properties
 * that link gift→parent (Deal A) or carry the discount % (Deal B, read by Discount Function).
 *
 * Reads gift product/variant data from the DOM (server-rendered in Liquid).
 * The initial deal is always whatever the block setting says — no per-session
 * persistence, so toggling deals during testing never bleeds into a customer's
 * next visit and the configured default is always the one shown.
 */
(function () {
  'use strict';

  /**
   * Pure helpers (no DOM) — unit-tested with `node --test tests/*.test.js`.
   * Bundle layout resolves per-slot option values to a variant through the
   * compact map rendered by snippets/deal-selector-variant-map.liquid.
   */
  const DealSelectorCore = {
    // Mirrors Liquid `money_without_trailing_zeros` for the common formats.
    formatMoney(cents, format) {
      const value = Math.round(Number(cents) || 0);
      const fmt = format || '{{amount}}';
      const group = (n, thousands, decimal) => {
        const whole = Math.floor(n / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        const frac = String(n % 100).padStart(2, '0');
        return frac === '00' ? whole : whole + decimal + frac;
      };
      return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
        switch (key) {
          case 'amount_no_decimals':
            return group(Math.round(value / 100) * 100, ',', '.');
          case 'amount_with_comma_separator':
            return group(value, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return group(Math.round(value / 100) * 100, '.', ',');
          case 'amount_with_space_separator':
            return group(value, ' ', ',');
          case 'amount_no_decimals_with_space_separator':
            return group(Math.round(value / 100) * 100, ' ', ',');
          case 'amount_with_period_and_space_separator':
            return group(value, ' ', '.');
          default:
            return group(value, ',', '.');
        }
      });
    },

    // Row: [id, price, compareAt, available(0|1), v1, v2, v3, mediaId]
    parseVariantMap(rows) {
      return (rows || []).map((r) => ({
        id: r[0],
        price: r[1],
        compareAt: r[2],
        available: r[3] === 1 || r[3] === true,
        options: [r[4], r[5], r[6]].filter((v) => v !== null && v !== undefined),
        mediaId: r[7] === undefined ? null : r[7],
      }));
    },

    resolveVariant(variants, selected) {
      for (let i = 0; i < variants.length; i++) {
        const v = variants[i];
        if (v.options.length !== selected.length) continue;
        let match = true;
        for (let j = 0; j < selected.length; j++) {
          if (v.options[j] !== selected[j]) {
            match = false;
            break;
          }
        }
        if (match) return v;
      }
      return null;
    },

    // Gallery slides shared by several variants (bundles) carry a
    // space-separated id list in data-variant-id — match it like the theme's
    // [data-variant-id~="…"] selector, not by equality.
    variantListHas(attr, id) {
      if (!attr) return false;
      return String(attr).split(/\s+/).includes(String(id));
    },

    // Which gallery slide/thumb stays visible for a variant, like the theme's
    // switchImage: the featured media always (its data-variant-id may name
    // just one of the variants sharing it), plus slides tagged with this
    // variant; untagged base media only as the fallback gallery.
    slideVisible(el, variantId, featuredId, hasVariantSlides) {
      if (featuredId && String(el.mediaId) === String(featuredId)) return true;
      if (el.variantIds) return DealSelectorCore.variantListHas(el.variantIds, variantId);
      return featuredId ? false : !hasVariantSlides;
    },

    // Only ids that exist in the map and are in stock may be posted to the cart.
    purchasableId(variants, id) {
      const v = variants.find((x) => x.id === id);
      return v && v.available ? v.id : null;
    },

    slotValueStates(variants, selected, slot, values) {
      return values.map((value) => {
        const probe = selected.slice();
        probe[slot] = value;
        const v = DealSelectorCore.resolveVariant(variants, probe);
        return { value, state: !v ? 'missing' : v.available ? 'available' : 'soldout' };
      });
    },

    discounted(cents, pct) {
      return Math.floor((cents * (100 - pct)) / 100);
    },

    // Shown price with the product's custom.discount %, same formula as
    // product-block-header.liquid: price - floor(price * pct / 100).
    effectivePrice(cents, pct) {
      return cents - Math.floor((cents * (pct || 0)) / 100);
    },

    // Deal B price. The product's automatic discount (custom.discount) and
    // the Deal B function do NOT stack — Shopify applies the larger one
    // (verified in cart: 5600 with 21% + 10% → 4424).
    dealBPrice(cents, productPct, dealBPct) {
      return DealSelectorCore.effectivePrice(cents, Math.max(productPct || 0, dealBPct || 0));
    },
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = DealSelectorCore;
    return;
  }
  window.DealSelectorCore = DealSelectorCore;

  if (window.customElements && customElements.get('deal-selector')) return;

  const ADD_URL = '/cart/add.js';

  function makeSessionId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  class DealSelector extends HTMLElement {
    constructor() {
      super();
      this.state = {
        deal: null,
        variantId: null,
        giftProductId: null,
        giftVariantId: null,
        giftCVariantId: null,
        busy: false,
      };
    }

    connectedCallback() {
      const configEl = this.querySelector('[data-deal-selector-config]');
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('[deal-selector] config parse error:', e);
        return;
      }

      this.applyDefaultDeal();
      this.captureInitialSelections();
      if (this.config.layout === 'bundle') this.initBundle();
      this.bindEvents();
      this.applyDealVisibility();
      this.updateButtonState();
      this.scheduleMediaPreload();
    }

    /* ---------------- state ---------------- */

    applyDefaultDeal() {
      // Deal selection is intentionally NOT persisted across visits. The block
      // setting `default_deal` (already coerced server-side to an enabled deal)
      // is the single source of truth for which deal is preselected on load.
      this.state.deal = this.config.defaultDeal;

      // Reflect the chosen deal in the radio inputs (Liquid also pre-checks
      // them, but a stale browser cache could land us here with the wrong
      // input checked — keep DOM and state in sync regardless).
      const toggles = this.querySelectorAll('[data-deal-toggle]');
      toggles.forEach((t) => {
        t.checked = t.value === this.state.deal;
      });
    }

    captureInitialSelections() {
      // Radio (standard layout) or hidden input (bundle layout seeds the
      // initial variant; later picks resolve through the variant map).
      const checkedVariant = this.querySelector(
        '[data-spf-variant]:checked, input[type="hidden"][data-spf-variant]'
      );
      if (checkedVariant) {
        this.state.variantId = parseInt(checkedVariant.value, 10);
      }
      // Deal C gift variant: pre-checked radio (multi-variant products) or a
      // hidden input (single "Default Title" products — no swatches rendered).
      const giftC = this.querySelector(
        '[data-gift-c-variant]:checked, input[type="hidden"][data-gift-c-variant]'
      );
      if (giftC) {
        this.state.giftCVariantId = parseInt(giftC.value, 10);
      }
    }

    /* ---------------- events ---------------- */

    bindEvents() {
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      document.addEventListener('click', (e) => {
        this.querySelectorAll('[data-ds-dropdown].is-open').forEach((dd) => {
          if (!dd.contains(e.target)) this.closeDropdown(dd);
        });
      });
    }

    onChange(e) {
      const t = e.target;
      if (t.matches('[data-deal-toggle]') && t.checked) {
        this.state.deal = t.value;
        this.applyDealVisibility();
        this.updatePrices();
        this.updateButtonState();
        return;
      }
      if (t.matches('[data-spf-variant]') && t.checked) {
        this.syncPickerFromRadio(t);
        this.state.variantId = parseInt(t.value, 10);
        this.updateGallery(this.state.variantId, t.dataset.featuredMediaId || null);
        this.updateButtonState();
        return;
      }
      if (t.matches('[data-gift-c-variant]') && t.checked) {
        this.syncPickerFromRadio(t);
        this.state.giftCVariantId = parseInt(t.value, 10);
        this.updateVariantRowDisplay(this.querySelector('[data-step-gift-c]'), t);
        this.updateButtonState();
        return;
      }
      if (t.matches('[data-gift-variant]')) {
        // Gift variant input changed — only update state if its product matches the selected gift.
        const productId = parseInt(t.dataset.giftProductId, 10);
        if (productId === this.state.giftProductId) {
          this.state.giftVariantId = parseInt(t.value, 10);
          this.updateGiftVariantDisplay(productId, t);
          this.updateButtonState();
        }
        return;
      }
    }

    onClick(e) {
      const trigger = e.target.closest('[data-ds-dropdown-trigger]');
      if (trigger && this.contains(trigger)) {
        const dd = trigger.closest('[data-ds-dropdown]');
        if (dd.classList.contains('is-open')) this.closeDropdown(dd);
        else this.openDropdown(dd);
        return;
      }
      const option = e.target.closest('[data-ds-option]');
      if (option && this.contains(option)) {
        this.pickOption(option);
        return;
      }
      const tab = e.target.closest('[data-ds-tabs] [data-tab-name]');
      if (tab && this.contains(tab)) {
        this.applyTab(tab);
        return;
      }
      const giftCard = e.target.closest('[data-gift-card]');
      if (giftCard && this.contains(giftCard)) {
        this.selectGift(giftCard);
        return;
      }
      if (e.target.closest('[data-add-to-bag]')) {
        e.preventDefault();
        this.addToCart();
      }
    }

    /* ---------------- dropdown + tabs (standard & bundle) ---------------- */

    openDropdown(dd) {
      this.querySelectorAll('[data-ds-dropdown].is-open').forEach((o) => this.closeDropdown(o));
      const menu = dd.querySelector('[data-ds-dropdown-menu]');
      if (!menu) return;
      menu.hidden = false;
      dd.classList.add('is-open');
      dd.querySelector('[data-ds-dropdown-trigger]').setAttribute('aria-expanded', 'true');
      const selected = menu.querySelector('[data-ds-option][aria-selected="true"]:not([hidden])');
      const first = selected || this.visibleOptions(menu)[0];
      if (first) first.focus({ preventScroll: true });
    }

    closeDropdown(dd, focusTrigger) {
      const menu = dd.querySelector('[data-ds-dropdown-menu]');
      if (menu) menu.hidden = true;
      dd.classList.remove('is-open');
      const trigger = dd.querySelector('[data-ds-dropdown-trigger]');
      trigger.setAttribute('aria-expanded', 'false');
      if (focusTrigger) trigger.focus();
    }

    visibleOptions(menu) {
      return Array.from(menu.querySelectorAll('[data-ds-option]')).filter(
        (o) => !o.hidden && o.getAttribute('aria-disabled') !== 'true'
      );
    }

    onKeydown(e) {
      const trigger = e.target.closest('[data-ds-dropdown-trigger]');
      if (trigger) {
        if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
          e.preventDefault();
          this.openDropdown(trigger.closest('[data-ds-dropdown]'));
        }
        return;
      }
      const option = e.target.closest('[data-ds-option]');
      if (!option) return;
      const dd = option.closest('[data-ds-dropdown]');
      const options = this.visibleOptions(dd.querySelector('[data-ds-dropdown-menu]'));
      const i = options.indexOf(option);
      let next = null;
      switch (e.key) {
        case 'ArrowDown':
          next = options[Math.min(i + 1, options.length - 1)];
          break;
        case 'ArrowUp':
          next = options[Math.max(i - 1, 0)];
          break;
        case 'Home':
          next = options[0];
          break;
        case 'End':
          next = options[options.length - 1];
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          this.pickOption(option);
          dd.querySelector('[data-ds-dropdown-trigger]').focus();
          return;
        case 'Escape':
          e.preventDefault();
          this.closeDropdown(dd, true);
          return;
        case 'Tab':
          this.closeDropdown(dd);
          return;
        default:
          return;
      }
      e.preventDefault();
      if (next) next.focus();
    }

    pickOption(option) {
      if (option.getAttribute('aria-disabled') === 'true') return;
      const dd = option.closest('[data-ds-dropdown]');
      if (option.dataset.slot !== undefined) {
        this.onSlotPick(option);
      } else {
        const picker = option.closest('[data-ds-picker]');
        const radio = picker && picker.querySelector(`input[type="radio"][value="${option.dataset.variantId}"]`);
        if (radio && !radio.disabled && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      this.closeDropdown(dd);
    }

    /* ---------------- bundle layout (one dropdown per slot) ---------------- */

    initBundle() {
      const mapEl = this.querySelector('[data-deal-variant-map]');
      try {
        this.variants = DealSelectorCore.parseVariantMap(JSON.parse(mapEl.textContent));
      } catch (e) {
        console.error('[deal-selector] variant map parse error:', e);
        this.variants = [];
      }
      const optionsOf = (id) => {
        const v = this.variants.find((x) => x.id === id);
        return v ? v.options.slice() : [];
      };
      this.bundleSel = {
        main: optionsOf(this.state.variantId),
        gift: optionsOf(this.state.giftCVariantId),
      };
      // Variant whose price is shown: the last combination that exists, so
      // prices don't blank out while a sold-out/missing combo is selected.
      this.displayVariantId = this.state.variantId;
      // The server-side initial variant may be sold out (?variant=…, or the
      // whole bundle sold out): keep its options shown but never post it.
      this.state.variantId = DealSelectorCore.purchasableId(this.variants, this.state.variantId);
      this.state.giftCVariantId = DealSelectorCore.purchasableId(this.variants, this.state.giftCVariantId);
      this.refreshSlots('main');
      this.refreshSlots('gift');
      this.updatePrices();
    }

    variantById(id) {
      return (this.variants || []).find((v) => v.id === id) || null;
    }

    onSlotPick(option) {
      const step = option.closest('[data-ds-slots]').dataset.step;
      const sel = this.bundleSel[step];
      sel[parseInt(option.dataset.slot, 10)] = option.dataset.value;
      const v = DealSelectorCore.resolveVariant(this.variants, sel);
      // A sold-out or nonexistent combination has no id → canSubmit() disables the CTA.
      const id = v && v.available ? v.id : null;
      if (step === 'main') {
        this.state.variantId = id;
        if (v) this.displayVariantId = v.id;
        if (v) this.updateGallery(v.id, v.mediaId ? String(v.mediaId) : null);
      } else {
        this.state.giftCVariantId = id;
      }
      this.refreshSlots(step);
      this.updatePrices();
      this.updateButtonState();
    }

    refreshSlots(step) {
      const container = this.querySelector(`[data-ds-slots][data-step="${step}"]`);
      if (!container) return;
      const sel = this.bundleSel[step];
      container.querySelectorAll('[data-slot-row]').forEach((row) => {
        const slot = parseInt(row.dataset.slotRow, 10);
        const options = Array.from(row.querySelectorAll('[data-ds-option]'));
        const states = DealSelectorCore.slotValueStates(
          this.variants,
          sel,
          slot,
          options.map((o) => o.dataset.value)
        );
        let chosen = null;
        options.forEach((o, i) => {
          const on = o.dataset.value === sel[slot];
          const unavailable = states[i].state !== 'available';
          o.setAttribute('aria-selected', on ? 'true' : 'false');
          o.classList.toggle('is-soldout', states[i].state === 'soldout');
          o.classList.toggle('is-missing', states[i].state === 'missing');
          // Values that would lead to a sold-out/missing combination stay
          // visible as an indicator but can't be picked (pickOption and the
          // keyboard navigation both skip aria-disabled options).
          if (unavailable && !on) o.setAttribute('aria-disabled', 'true');
          else o.removeAttribute('aria-disabled');
          if (on) chosen = o;
        });
        if (!chosen) return;
        const current = row.querySelector('[data-ds-dropdown-current]');
        if (current) current.innerHTML = chosen.innerHTML;
        const trigger = row.querySelector('[data-ds-dropdown-trigger]');
        const unavailable = chosen.classList.contains('is-soldout') || chosen.classList.contains('is-missing');
        if (trigger) trigger.classList.toggle('is-unavailable', unavailable);
        const src = chosen.dataset.image;
        if (src) {
          let img = row.querySelector('[data-slot-image]');
          if (!img) {
            img = document.createElement('img');
            img.setAttribute('data-slot-image', '');
            img.alt = '';
            img.width = 80;
            img.height = 80;
            row.querySelector('.deal-selector__slot-media').appendChild(img);
          }
          if (img.getAttribute('src') !== src) img.src = src;
        }
      });
    }

    // Bundle layout shows what the customer pays in the offer cards and the CTA.
    updatePrices() {
      if (this.config.layout !== 'bundle') return;
      const v = this.variantById(this.state.variantId || this.displayVariantId);
      const fmt = (c) => DealSelectorCore.formatMoney(c, this.config.moneyFormat);
      // Selected main/gift combination doesn't exist or is sold out: the CTA
      // reads "Не е наличен" instead of silently greying out.
      const comboMissing =
        !this.state.variantId || (this.state.deal === 'c' && !this.state.giftCVariantId);
      this.toggleAttribute('data-combo-unavailable', comboMissing);
      const productPct = this.config.productDiscountPct;
      const priceFor = (deal) =>
        deal === 'b'
          ? DealSelectorCore.dealBPrice(v.price, productPct, this.config.discountPct)
          : DealSelectorCore.effectivePrice(v.price, productPct);
      this.querySelectorAll('[data-offer-price]').forEach((el) => {
        el.textContent = v ? fmt(priceFor(el.dataset.deal)) : '';
      });
      const total = v ? priceFor(this.state.deal) : null;
      this.querySelectorAll('[data-cta-total]').forEach((el) => {
        el.textContent = total === null ? '' : ' – ' + fmt(total);
      });
    }

    // Swatch radio changed (or set by the dropdown): mirror it into the listbox.
    syncPickerFromRadio(radio) {
      const picker = radio.closest('[data-ds-picker]');
      if (!picker) return;
      let chosen = null;
      picker.querySelectorAll('[data-ds-option]').forEach((o) => {
        const on = o.dataset.variantId === radio.value;
        o.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) chosen = o;
      });
      const current = picker.querySelector('[data-ds-dropdown-current]');
      if (current && chosen) current.innerHTML = chosen.innerHTML;
    }

    // Tabs only filter what's visible; the selection never changes here.
    applyTab(tab) {
      const picker = tab.closest('[data-ds-picker]');
      picker.querySelectorAll('[data-ds-tabs] [data-tab-name]').forEach((t) => {
        t.classList.toggle('is-active', t === tab);
      });
      const ids = tab.dataset.tabVariants ? new Set(tab.dataset.tabVariants.split(',')) : null;
      picker.querySelectorAll('[data-variant-option], [data-ds-option]').forEach((el) => {
        el.hidden = !!ids && !ids.has(el.dataset.variantId);
      });
    }

    selectGift(cardEl) {
      const productId = parseInt(cardEl.dataset.giftProductId, 10);
      const hasVariants = cardEl.dataset.hasVariants === 'true';

      // Visual: deselect sibling cards, mark this one selected.
      const grid = cardEl.parentElement;
      grid.querySelectorAll('[data-gift-card]').forEach((c) => {
        c.classList.toggle('is-selected', c === cardEl);
      });

      // Show the matching variant row container; hide the others.
      const variantRow = this.querySelector('[data-gift-variant-row]');
      if (variantRow) {
        let anyVisible = false;
        variantRow.querySelectorAll('[data-gift-variant-row-for]').forEach((row) => {
          const match = parseInt(row.dataset.giftVariantRowFor, 10) === productId;
          row.hidden = !match;
          if (match) anyVisible = true;
        });
        variantRow.hidden = !anyVisible;
      }

      // Capture the initial variant ID for this gift.
      let variantId = null;
      if (hasVariants) {
        const checkedRadio = this.querySelector(
          `[data-gift-variant-row-for="${productId}"] [data-gift-variant]:checked`
        );
        if (checkedRadio) variantId = parseInt(checkedRadio.value, 10);
      } else {
        const hiddenInput = cardEl.querySelector('[data-gift-variant]');
        if (hiddenInput) variantId = parseInt(hiddenInput.value, 10);
      }

      this.state.giftProductId = productId;
      this.state.giftVariantId = variantId;
      this.updateButtonState();
    }

    updateGiftVariantDisplay(productId, inputEl) {
      this.updateVariantRowDisplay(this.querySelector(`[data-gift-variant-row-for="${productId}"]`), inputEl);
    }

    updateVariantRowDisplay(row, inputEl) {
      if (!row) return;
      const valueEl = row.querySelector('[data-gift-variant-current-value]');
      if (valueEl) valueEl.textContent = inputEl.dataset.giftVariantTitle || inputEl.value;

      const imageUrl = inputEl.dataset.giftVariantImage;
      if (imageUrl) {
        const img = row.querySelector('[data-gift-variant-row-image]');
        if (img) img.src = imageUrl;
      }
    }

    /* ---------------- media preload ---------------- */

    /**
     * Warm the browser cache for the hidden variant-gallery images so the
     * first variant switch doesn't jump while the network catches up.
     *
     * The hidden slides are display:none with loading="lazy", so the browser
     * never fetches them until they're revealed. Flipping them to eager (at
     * low fetch priority) makes the browser load them with its own
     * srcset/sizes selection — the exact URL it will need later, straight
     * from cache on switch.
     *
     * Deliberately conservative: only runs on deal-selector PDPs (this script
     * loads only there), starts after the window load event so it can't
     * compete with LCP/critical resources, feeds the queue in small batches
     * on idle callbacks, and skips entirely on Save-Data or 2G connections.
     * Order: each variant's first gallery image first (that's what shows at
     * the top on switch), then the rest, then the thumbnail strip.
     */
    scheduleMediaPreload() {
      if (window.__dealSelectorMediaPreload) return;
      window.__dealSelectorMediaPreload = true;

      const conn = navigator.connection;
      if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return;

      const start = () => {
        const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
        idle(() => this.pumpPreloadQueue(this.collectPreloadQueue()));
      };
      if (document.readyState === 'complete') {
        start();
      } else {
        window.addEventListener('load', start, { once: true });
      }

      // Intent boost: hovering/touching a variant row fetches that variant's
      // images immediately — the hover-to-click gap usually covers the load.
      this.addEventListener('pointerover', (e) => {
        const option = e.target.closest('[data-variant-option]');
        if (!option) return;
        const input = option.querySelector('[data-spf-variant]');
        if (input) this.preloadVariantNow(input.value);
      });
    }

    collectPreloadQueue() {
      const firstPerVariant = [];
      const rest = [];
      const seenVariants = new Set();
      document
        .querySelectorAll('.product__media-item[data-variant-id] img[loading="lazy"]')
        .forEach((img) => {
          const vid = img.closest('[data-variant-id]').dataset.variantId;
          if (seenVariants.has(vid)) {
            rest.push(img);
          } else {
            seenVariants.add(vid);
            firstPerVariant.push(img);
          }
        });
      const thumbs = Array.from(
        document.querySelectorAll('[data-thumbnail-id][data-variant-id] img[loading="lazy"]')
      );
      return firstPerVariant.concat(rest, thumbs);
    }

    pumpPreloadQueue(queue) {
      const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
      const step = () => {
        let n = 0;
        while (queue.length && n < 4) {
          this.eagerizeImage(queue.shift(), 'low');
          n++;
        }
        if (queue.length) idle(step);
      };
      step();
    }

    preloadVariantNow(variantId) {
      document
        .querySelectorAll(
          `.product__media-item[data-variant-id~="${variantId}"] img[loading="lazy"],` +
            `[data-thumbnail-id][data-variant-id~="${variantId}"] img[loading="lazy"]`
        )
        .forEach((img) => this.eagerizeImage(img, 'auto'));
    }

    eagerizeImage(img, priority) {
      if (!img || img.loading !== 'lazy') return;
      img.setAttribute('fetchpriority', priority || 'low');
      img.decoding = 'async';
      img.loading = 'eager';
    }

    /**
     * Swap the PDP gallery to the selected variant's media (featured image +
     * the variant's `custom.image_gallery` slides, server-rendered with
     * data-variant-id — see main-product--default.liquid).
     *
     * Faithful re-implementation of the theme's own variant-change media flow
     * (theme.js onVariantChange → switchImage): show the variant's featured
     * media + its gallery slides, hide the rest, then refresh the GLOBAL
     * swiper instances the theme uses — window.mainSwiper for the media and
     * window.thumbnailSwiper for the thumbnail strip, re-binding thumb clicks
     * to the new slide indexes exactly like the theme does. Never runs
     * update/slideTo against a swiper found by generic DOM search: in
     * thumbnails mode that would hit the thumbnail swiper and break it.
     */
    updateGallery(variantId, featuredMediaId) {
      const idStr = String(variantId);
      const featuredId = featuredMediaId ? String(featuredMediaId) : null;

      document.querySelectorAll('.product__media-container').forEach((container) => {
        const slides = container.querySelectorAll('.product__media-item');
        if (!slides.length) return;
        const hasVariantSlides = !!container.querySelector(
          `.product__media-item[data-variant-id~="${idStr}"]`
        );
        // Nothing to switch to for this variant — leave the gallery alone
        // (same skip as the theme's `if (variant.featured_media)` guard).
        if (!hasVariantSlides && !featuredId) return;

        slides.forEach((item) => {
          // visually-hidden marks the theme's deliberate duplicates — never reveal them.
          const show =
            !item.classList.contains('visually-hidden') &&
            DealSelectorCore.slideVisible(
              { variantIds: item.dataset.variantId, mediaId: item.dataset.mediaItemId },
              idStr,
              featuredId,
              hasVariantSlides
            );
          if (show) {
            item.classList.remove('hide');
            item.classList.remove('hidden');
            // switchImage adds swiper-slide to every slide it reveals (the
            // desktop markup renders hidden variant slides without it).
            if (item.classList.contains('carousel_slide') || container.querySelector('.swiper-wrapper')) {
              item.classList.add('swiper-slide');
            }
          } else {
            // switchImage hides via `hidden` and leaves other classes alone.
            item.classList.add('hidden');
            item.classList.add('hide');
          }
        });

        // Mobile carousel: the container itself is the swiper element.
        if (container.classList.contains('swiper') && container.swiper) {
          container.swiper.update();
          container.swiper.slideTo(0, 0);
        }
      });

      // Desktop media swiper — the theme drives this through the global
      // instance (see switchImage's mainSwiper block).
      if (window.mainSwiper && document.querySelector('.product__media.swiper')) {
        window.mainSwiper.update();
        if (typeof window.mainSwiper.updateSlides === 'function') window.mainSwiper.updateSlides();
        if (typeof window.mainSwiper.updateSlidesClasses === 'function') window.mainSwiper.updateSlidesClasses();
        window.mainSwiper.slideTo(0, 0);
      }

      // Thumbnail strip (thumbnails gallery style): mirror the theme's
      // onVariantChange — toggle thumb visibility + their slide wrappers'
      // swiper-slide class, mark the featured thumb active, refresh
      // window.thumbnailSwiper and re-bind slide clicks to the new indexes.
      const allThumbs = document.querySelectorAll('[data-thumbnail-id]');
      if (allThumbs.length) {
        const hasVariantThumbs = !!document.querySelector(
          `[data-thumbnail-id][data-variant-id~="${idStr}"]`
        );
        if (hasVariantThumbs || featuredId) {
          allThumbs.forEach((thumb) => {
            const show =
              !thumb.classList.contains('visually-hidden') &&
              DealSelectorCore.slideVisible(
                { variantIds: thumb.dataset.variantId, mediaId: thumb.dataset.thumbnailId },
                idStr,
                featuredId,
                hasVariantThumbs
              );
            const slide = thumb.closest('.product-thumbnails__item');
            thumb.classList.toggle('hide', !show);
            thumb.classList.remove('active');
            if (slide) {
              slide.classList.toggle('swiper-slide', show);
              slide.classList.remove('swiper-slide-active');
            }
          });

          const featuredThumb = featuredId
            ? document.querySelector(`[data-thumbnail-id="${featuredId}"]:not(.visually-hidden)`)
            : null;
          if (featuredThumb && !featuredThumb.classList.contains('hide')) {
            featuredThumb.classList.add('active');
            const featuredSlide = featuredThumb.closest('.product-thumbnails__item');
            if (featuredSlide) featuredSlide.classList.add('swiper-slide-active');
          }

          if (window.thumbnailSwiper) {
            window.thumbnailSwiper.update();
            if (typeof window.thumbnailSwiper.updateSlides === 'function') window.thumbnailSwiper.updateSlides();
            if (typeof window.thumbnailSwiper.updateSlidesClasses === 'function') window.thumbnailSwiper.updateSlidesClasses();
            // Same re-bind the theme does after each variant change: thumb
            // slide index N navigates the main swiper to slide N. Listeners
            // stack across changes (as in theme.js); the newest runs last,
            // so the final slideTo target is always the current index.
            window.thumbnailSwiper.slides.forEach((slide, index) => {
              slide.addEventListener('click', function (event) {
                event.preventDefault();
                if (window.mainSwiper) window.mainSwiper.slideTo(index);
              });
            });
          }
        }
      }
    }

    /* ---------------- visibility / button ---------------- */

    applyDealVisibility() {
      this.toggleAttribute('data-deal-active-a', this.state.deal === 'a');
      this.toggleAttribute('data-deal-active-b', this.state.deal === 'b');
      this.toggleAttribute('data-deal-active-c', this.state.deal === 'c');
      const giftStep = this.querySelector('[data-step-gift]');
      if (giftStep) giftStep.hidden = this.state.deal !== 'a';
      const giftCStep = this.querySelector('[data-step-gift-c]');
      if (giftCStep) giftCStep.hidden = this.state.deal !== 'c';
    }

    canSubmit() {
      if (this.state.busy) return false;
      if (!this.state.variantId) return false;
      if (this.state.deal === 'a') {
        if (!this.state.giftProductId) return false;
        if (!this.state.giftVariantId) return false;
      }
      if (this.state.deal === 'c' && !this.state.giftCVariantId) return false;
      return !!this.state.deal;
    }

    updateButtonState() {
      const btn = this.querySelector('[data-add-to-bag]');
      if (!btn) return;
      btn.disabled = !this.canSubmit();
      btn.setAttribute('aria-busy', this.state.busy ? 'true' : 'false');
    }

    /* ---------------- cart API ---------------- */

    buildCartPayload(sessionId) {
      if (this.state.deal === 'b') {
        return [
          {
            id: this.state.variantId,
            quantity: 1,
            properties: { _deal: 'b', _deal_discount_pct: String(this.config.discountPct) },
          },
        ];
      }

      // Deals A and C: parent line + gift line linked via session ID.
      const parent = {
        id: this.state.variantId,
        quantity: 1,
        properties: { _deal: this.state.deal, _deal_session: sessionId },
      };

      const gift = {
        id: this.state.deal === 'c' ? this.state.giftCVariantId : this.state.giftVariantId,
        quantity: 1,
        properties: { _gift_for: sessionId, _free_gift: 'Yes' },
      };
      if (this.state.deal === 'c') {
        // Deal C gifts are PAID variants of the same product — the Discount
        // Function reads this pct (validated server-side) to zero the line.
        gift.properties._deal_discount_pct = String(this.config.dealCDiscountPct);
      }
      // Gift first: the cart drawer lists the most recently added line on
      // top, and the items array is processed in order — so the PAID parent
      // (added last) shows above the free gift.
      return [gift, parent];
    }

    async addToCart() {
      if (!this.canSubmit()) return;
      this.state.busy = true;
      this.updateButtonState();
      this.clearError();

      const sessionId = makeSessionId();
      const items = this.buildCartPayload(sessionId);

      try {
        const res = await fetch(ADD_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) {
          const errPayload = await res.json().catch(() => ({}));
          throw new Error(errPayload.description || errPayload.message || 'Add to cart failed');
        }
        const cartItem = await res.json().catch(() => ({}));

        this.notifyCartUpdated(cartItem);
        this.openQuickCart();
      } catch (err) {
        console.error('[deal-selector] addToCart error:', err);
        this.showError(err && err.message ? err.message : 'Could not add to cart');
      } finally {
        this.state.busy = false;
        this.updateButtonState();
      }
    }

    notifyCartUpdated(cartItem) {
      // `apps:product-added-to-cart` on document is what the theme drawer
      // (theme.js `register("quick-cart", ...)`) listens to in order to
      // re-fetch `?section_id=quick-cart` and swap in fresh markup. It's
      // also what the cart-count updater listens to. The misleadingly-
      // named `change` event on `.quick-cart` and the document
      // `cart:updated` event have NO listeners in the theme — kept here
      // only as compat hooks for any merchant analytics that subscribed.
      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: cartItem } }));
      document.dispatchEvent(new CustomEvent('apps:product-added-to-cart', { detail: {} }));
    }

    openQuickCart() {
      // Theme.js binds the cart-drawer-open action exclusively to its internal
      // `quick-cart:open` event, fired from a click handler on `[data-js-cart-icon]`.
      // Synthesizing a click on that icon is the simplest way to trigger the open
      // without coupling to internal theme module state.
      const cartIcon = document.querySelector('[data-js-cart-icon]');
      if (cartIcon) {
        // Slight delay so the drawer markup refresh (triggered above) settles first,
        // matching what the theme's own product form handler does (300ms).
        setTimeout(() => {
          cartIcon.click();
        }, 300);
      }
    }

    showError(msg) {
      const el = this.querySelector('[data-deal-error]');
      if (!el) return;
      el.textContent = msg;
      el.hidden = false;
    }

    clearError() {
      const el = this.querySelector('[data-deal-error]');
      if (!el) return;
      el.textContent = '';
      el.hidden = true;
    }
  }

  customElements.define('deal-selector', DealSelector);
})();
